import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import ProviderSidebar from '../../components/ui/ProviderSidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import DashboardStats from './components/DashboardStats';
import PatientListTable from './components/PatientListTable';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isAISummaryLoading, setIsAISummaryLoading] = useState(false);
  const [aiSummaryModal, setAiSummaryModal] = useState({ isOpen: false, patient: null, summary: '' });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Mock provider data
  const providerData = {
    name: "Dr. Sarah Mitchell",
    role: "provider",
    specialization: "Neurologist"
  };

  // Mock patient data with comprehensive health information
  const mockPatients = [
    {
      id: 'P001',
      name: 'Sarah Johnson',
      lastActivity: '2024-08-24T11:30:00',
      avgSeverity: 6.8,
      riskLevel: 'high',
      isActiveToday: true,
      hasAlerts: true,
      age: 45,
      diagnosis: 'Multiple Sclerosis',
      recentSymptoms: ['Fatigue', 'Cognitive fog', 'Balance issues'],
      cognitiveScores: [78, 82, 75, 80, 77]
    },
    {
      id: 'P002',
      name: 'Michael Chen',
      lastActivity: '2024-08-24T09:15:00',
      avgSeverity: 4.2,
      riskLevel: 'medium',
      isActiveToday: true,
      hasAlerts: false,
      age: 38,
      diagnosis: 'Migraine Disorder',
      recentSymptoms: ['Headache', 'Light sensitivity'],
      cognitiveScores: [85, 88, 90, 87, 89]
    },
    {
      id: 'P003',
      name: 'Emma Rodriguez',
      lastActivity: '2024-08-23T16:45:00',
      avgSeverity: 8.1,
      riskLevel: 'high',
      isActiveToday: false,
      hasAlerts: true,
      age: 52,
      diagnosis: 'Parkinsons Disease',
      recentSymptoms: ['Tremor', 'Stiffness', 'Sleep disturbance'],
      cognitiveScores: [72, 70, 68, 74, 71]
    },
    {
      id: 'P004',
      name: 'David Thompson',
      lastActivity: '2024-08-24T08:20:00',
      avgSeverity: 3.5,
      riskLevel: 'low',
      isActiveToday: true,
      hasAlerts: false,
      age: 29,
      diagnosis: 'Post-Concussion Syndrome',
      recentSymptoms: ['Mild headache', 'Concentration issues'],
      cognitiveScores: [92, 94, 91, 95, 93]
    },
    {
      id: 'P005',
      name: 'Lisa Wang',
      lastActivity: '2024-08-24T10:10:00',
      avgSeverity: 5.3,
      riskLevel: 'medium',
      isActiveToday: true,
      hasAlerts: false,
      age: 41,
      diagnosis: 'Epilepsy',
      recentSymptoms: ['Aura symptoms', 'Memory lapses'],
      cognitiveScores: [81, 83, 79, 85, 82]
    }
  ];

  // Dashboard statistics
  const dashboardStats = {
    totalPatients: mockPatients?.length,
    activeToday: mockPatients?.filter(p => p?.isActiveToday)?.length,
    highRiskPatients: mockPatients?.filter(p => p?.riskLevel === 'high')?.length,
    avgSeverityScore: (mockPatients?.reduce((sum, p) => sum + p?.avgSeverity, 0) / mockPatients?.length)?.toFixed(1),
    newAlertsCount: mockPatients?.filter(p => p?.hasAlerts)?.length,
    completedAssessments: 156
  };

  // Breadcrumb configuration
  const breadcrumbItems = [
    { label: 'Provider Dashboard', path: '/provider-dashboard', clickable: false }
  ];

  // Handle patient selection
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    navigate('/detailed-patient-view', { state: { patientId: patient?.id, patientData: patient } });
  };

  // Handle AI summary generation
  const handleGenerateAISummary = async (patient) => {
    setIsAISummaryLoading(true);
    setAiSummaryModal({ isOpen: true, patient, summary: '' });

    try {
      // Simulate AI API call
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const mockAISummary = `**Patient Health Analysis for ${patient?.name}**\n\n**Current Status:** ${patient?.riskLevel?.charAt(0)?.toUpperCase() + patient?.riskLevel?.slice(1)} risk patient with average symptom severity of ${patient?.avgSeverity}/10.\n\n**Key Findings:**\n• Recent symptom patterns show ${patient?.avgSeverity > 6 ? 'concerning elevation' : 'stable management'} in primary symptoms\n• Cognitive performance trending ${patient?.cognitiveScores?.[4] > patient?.cognitiveScores?.[0] ? 'upward' : 'downward'} with latest score of ${patient?.cognitiveScores?.[4]}%\n• Patient engagement is ${patient?.isActiveToday ? 'excellent' : 'needs improvement'} with regular system usage\n\n**Recommendations:**\n• ${patient?.riskLevel === 'high' ? 'Schedule immediate follow-up appointment' : 'Continue current treatment plan'}\n• Monitor ${patient?.recentSymptoms?.join(', ')?.toLowerCase()} symptoms closely\n• Consider ${patient?.cognitiveScores?.[4] < 80 ? 'cognitive rehabilitation therapy' : 'maintaining current cognitive exercises'}\n\n**Correlation Analysis:**\nSymptom severity appears to correlate with ${patient?.cognitiveScores?.[4] < 80 ? 'decreased' : 'maintained'} cognitive performance, suggesting ${patient?.riskLevel === 'high' ? 'need for intervention adjustment' : 'effective current management'}.`;

      setAiSummaryModal(prev => ({ ...prev, summary: mockAISummary }));
    } catch (error) {
      console.error('AI Summary generation failed:', error);
      setAiSummaryModal(prev => ({ 
        ...prev, 
        summary: 'Failed to generate AI summary. Please try again later.' 
      }));
    } finally {
      setIsAISummaryLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    // Clear all authentication data from localStorage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    
    // Navigate to login screen
    navigate('/login-screen');
  };

  // Handle profile click
  const handleProfileClick = () => {
    console.log('Profile settings clicked');
  };

  // Quick action handlers
  const handleExportReports = async () => {
    console.log('Exporting reports...');
    // Simulate export process
    return new Promise(resolve => setTimeout(resolve, 2000));
  };

  const handleGenerateBulkAI = async () => {
    console.log('Generating bulk AI analysis...');
    // Simulate bulk AI generation
    return new Promise(resolve => setTimeout(resolve, 3000));
  };

  const handleViewAlerts = () => {
    console.log('Viewing all alerts...');
  };

  const handlePatientSearch = () => {
    console.log('Opening advanced search...');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <RoleBasedHeader
        userRole={providerData?.role}
        userName={providerData?.name}
        onLogout={handleLogout}
        onProfileClick={handleProfileClick}
      />
      {/* Sidebar */}
      <ProviderSidebar
        isCollapsed={sidebarCollapsed}
        patients={mockPatients}
        selectedPatientId={selectedPatient?.id}
        onPatientSelect={handlePatientSelect}
      />
      {/* Main Content */}
      <main className={`
        pt-16 transition-all duration-300
        ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-80'}
      `}>
        <div className="p-6 space-y-6">
          {/* Breadcrumb */}
          <BreadcrumbTrail items={breadcrumbItems} />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Provider Dashboard</h1>
              <p className="text-muted-foreground mt-1">
                Monitor patient health data and generate AI-powered insights
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                iconName={sidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'}
                className="hidden lg:flex"
              />
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
              >
                Add Patient
              </Button>
            </div>
          </div>

          {/* Dashboard Statistics */}
          <DashboardStats stats={dashboardStats} />

          {/* Quick Actions */}
          <QuickActions
            onExportReports={handleExportReports}
            onGenerateBulkAI={handleGenerateBulkAI}
            onViewAlerts={handleViewAlerts}
            onPatientSearch={handlePatientSearch}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Patient List Table */}
            <div className="xl:col-span-2">
              <PatientListTable
                patients={mockPatients}
                onPatientSelect={handlePatientSelect}
                onGenerateAISummary={handleGenerateAISummary}
              />
            </div>

            {/* Recent Activity */}
            <div className="xl:col-span-1">
              <RecentActivity />
            </div>
          </div>
        </div>
      </main>
      {/* AI Summary Modal */}
      {aiSummaryModal?.isOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[1100]" 
            onClick={() => setAiSummaryModal({ isOpen: false, patient: null, summary: '' })}
          />
          <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 md:w-2/3 md:max-w-4xl bg-card border border-border rounded-lg shadow-soft-lg z-[1100] animate-scale-in max-h-[90vh] overflow-hidden">
            <div className="flex flex-col h-full">
              {/* Modal Header */}
              <div className="p-6 border-b border-border flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Brain" size={20} color="var(--color-accent)" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">AI Health Summary</h3>
                    <p className="text-sm text-muted-foreground">
                      {aiSummaryModal?.patient?.name} - Generated Analysis
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAiSummaryModal({ isOpen: false, patient: null, summary: '' })}
                  iconName="X"
                />
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {isAISummaryLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <div className="animate-spin">
                      <Icon name="Loader2" size={32} color="var(--color-primary)" />
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-medium text-foreground">Generating AI Analysis...</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Analyzing 30-day symptom and cognitive data patterns
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="prose prose-sm max-w-none">
                    <div className="bg-muted/30 rounded-lg p-4 whitespace-pre-line text-foreground">
                      {aiSummaryModal?.summary}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-border flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setAiSummaryModal({ isOpen: false, patient: null, summary: '' })}
                >
                  Close
                </Button>
                {!isAISummaryLoading && aiSummaryModal?.summary && (
                  <Button
                    variant="default"
                    iconName="Download"
                    iconPosition="left"
                  >
                    Export Summary
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProviderDashboard;